/**
 * Hand-drawn / "sketch" shapes inspired by Excalidraw — lightweight, no extra deps.
 * Uses a seeded RNG so preview while dragging matches the final stroke.
 */

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function roughSegment(ctx, x1, y1, x2, y2, lw, rng, jitterScale) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.hypot(dx, dy) || 1
  const nx = -dy / len
  const ny = dx / len
  const segments = Math.max(4, Math.min(28, Math.floor(len / 9)))
  const jitter = Math.max(0.6, jitterScale * lw)

  ctx.lineWidth = lw
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  for (let i = 1; i <= segments; i++) {
    const t = i / segments
    let x = x1 + dx * t
    let y = y1 + dy * t
    if (i < segments) {
      const j = (rng() * 2 - 1) * jitter
      x += nx * j
      y += ny * j
    }
    ctx.lineTo(x, y)
  }
  ctx.stroke()
}

function strokeRoughLine(ctx, x1, y1, x2, y2, color, lineWidth, rng) {
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.globalCompositeOperation = 'source-over'
  roughSegment(ctx, x1, y1, x2, y2, lineWidth, rng, 0.45)
  ctx.globalAlpha = 0.28
  roughSegment(ctx, x1, y1, x2, y2, lineWidth * 0.55, rng, 0.55)
  ctx.globalAlpha = 1
  ctx.restore()
}

export function drawRoughLine(ctx, x1, y1, x2, y2, color, lineWidth, seed) {
  strokeRoughLine(ctx, x1, y1, x2, y2, color, lineWidth, mulberry32(seed >>> 0))
}

export function drawRoughArrow(ctx, x1, y1, x2, y2, color, lineWidth, seed) {
  const rng = mulberry32(seed >>> 0)
  strokeRoughLine(ctx, x1, y1, x2, y2, color, lineWidth, rng)

  const angle = Math.atan2(y2 - y1, x2 - x1)
  const headLen = Math.max(14, lineWidth * 4.2)
  const spread = 0.42
  const j1 = (rng() - 0.5) * 4
  const j2 = (rng() - 0.5) * 4

  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = lineWidth
  ctx.globalCompositeOperation = 'source-over'
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - headLen * Math.cos(angle - spread) + j1, y2 - headLen * Math.sin(angle - spread) + j2)
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - headLen * Math.cos(angle + spread) + j2, y2 - headLen * Math.sin(angle + spread) + j1)
  ctx.stroke()

  ctx.globalAlpha = 0.2
  ctx.beginPath()
  ctx.moveTo(x2, y2)
  ctx.lineTo(x2 - headLen * 0.88 * Math.cos(angle - spread * 0.9), y2 - headLen * 0.88 * Math.sin(angle - spread * 0.9))
  ctx.stroke()
  ctx.globalAlpha = 1
  ctx.restore()
}

export function drawRoughRect(ctx, minX, minY, w, h, color, lineWidth, seed) {
  if (w < 2 && h < 2) return
  const rng = mulberry32(seed >>> 0)
  const j = () => (rng() - 0.5) * Math.max(1.2, lineWidth * 0.35)
  const x0 = minX + j()
  const y0 = minY + j()
  const x1 = minX + w + j()
  const y1 = minY + j()
  const x2 = minX + w + j()
  const y2 = minY + h + j()
  const x3 = minX + j()
  const y3 = minY + h + j()

  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.globalCompositeOperation = 'source-over'
  roughSegment(ctx, x0, y0, x1, y1, lineWidth, rng, 0.5)
  roughSegment(ctx, x1, y1, x2, y2, lineWidth, rng, 0.5)
  roughSegment(ctx, x2, y2, x3, y3, lineWidth, rng, 0.5)
  roughSegment(ctx, x3, y3, x0, y0, lineWidth, rng, 0.5)
  ctx.globalAlpha = 0.22
  const k = lineWidth * 0.5
  roughSegment(ctx, x0 + k, y0, x1 - k, y1, lineWidth * 0.45, rng, 0.55)
  ctx.globalAlpha = 1
  ctx.restore()
}

export function drawRoughEllipse(ctx, cx, cy, rx, ry, color, lineWidth, seed) {
  if (rx < 2 && ry < 2) return
  const rng = mulberry32(seed >>> 0)
  const steps = Math.max(16, Math.min(56, Math.floor((rx + ry) / 4)))
  const pts = []
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2
    const j = (rng() - 0.5) * Math.max(1, lineWidth * 0.28)
    pts.push({
      x: cx + rx * Math.cos(t) + j * Math.cos(t * 1.7),
      y: cy + ry * Math.sin(t) + j * Math.sin(t * 1.3),
    })
  }
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.lineWidth = lineWidth
  ctx.globalCompositeOperation = 'source-over'
  ctx.beginPath()
  ctx.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
  ctx.closePath()
  ctx.stroke()
  ctx.globalAlpha = 0.24
  ctx.lineWidth = lineWidth * 0.48
  ctx.stroke()
  ctx.globalAlpha = 1
  ctx.restore()
}

export function drawRoughDiamond(ctx, minX, minY, w, h, color, lineWidth, seed) {
  if (w < 2 && h < 2) return
  const rng = mulberry32(seed >>> 0)
  const mx = minX + w / 2
  const my = minY + h / 2
  const j = () => (rng() - 0.5) * Math.max(1.5, lineWidth * 0.4)
  const top = { x: mx + j(), y: minY + j() }
  const right = { x: minX + w + j(), y: my + j() }
  const bottom = { x: mx + j(), y: minY + h + j() }
  const left = { x: minX + j(), y: my + j() }

  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.globalCompositeOperation = 'source-over'
  roughSegment(ctx, top.x, top.y, right.x, right.y, lineWidth, rng, 0.52)
  roughSegment(ctx, right.x, right.y, bottom.x, bottom.y, lineWidth, rng, 0.52)
  roughSegment(ctx, bottom.x, bottom.y, left.x, left.y, lineWidth, rng, 0.52)
  roughSegment(ctx, left.x, left.y, top.x, top.y, lineWidth, rng, 0.52)
  ctx.restore()
}

/** מקבילית — קלט/פלט תרשים זרימה */
export function drawRoughParallelogram(ctx, minX, minY, w, h, color, lineWidth, seed) {
  if (w < 2 && h < 2) return
  const rng = mulberry32(seed >>> 0)
  const skew = Math.min(w * 0.22, h * 0.35)
  const j = () => (rng() - 0.5) * Math.max(1, lineWidth * 0.35)
  const p0 = { x: minX + skew + j(), y: minY + j() }
  const p1 = { x: minX + w + j(), y: minY + j() }
  const p2 = { x: minX + w - skew + j(), y: minY + h + j() }
  const p3 = { x: minX + j(), y: minY + h + j() }

  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  roughSegment(ctx, p0.x, p0.y, p1.x, p1.y, lineWidth, rng, 0.5)
  roughSegment(ctx, p1.x, p1.y, p2.x, p2.y, lineWidth, rng, 0.5)
  roughSegment(ctx, p2.x, p2.y, p3.x, p3.y, lineWidth, rng, 0.5)
  roughSegment(ctx, p3.x, p3.y, p0.x, p0.y, lineWidth, rng, 0.5)
  ctx.globalAlpha = 0.2
  roughSegment(ctx, p0.x + lineWidth * 0.35, p0.y, p1.x - lineWidth * 0.25, p1.y, lineWidth * 0.45, rng, 0.55)
  ctx.globalAlpha = 1
  ctx.restore()
}

/** מלבן מעוגל — התחלה/סיום */
export function drawRoughRoundRect(ctx, minX, minY, w, h, color, lineWidth, seed) {
  if (w < 2 && h < 2) return
  const rng = mulberry32(seed >>> 0)
  const r = Math.min(w, h) * 0.14
  const j = () => (rng() - 0.5) * Math.max(0.8, lineWidth * 0.32)

  const segmentsArc = (cx, cy, radius, a0, a1) => {
    const steps = Math.max(6, Math.floor((Math.abs(a1 - a0) * radius) / 5))
    const pts = []
    for (let i = 0; i <= steps; i++) {
      const t = a0 + ((a1 - a0) * i) / steps
      pts.push({
        x: cx + radius * Math.cos(t) + j(),
        y: cy + radius * Math.sin(t) + j(),
      })
    }
    for (let i = 1; i < pts.length; i++) {
      roughSegment(ctx, pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y, lineWidth, rng, 0.48)
    }
  }

  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  roughSegment(ctx, minX + r + j(), minY + j(), minX + w - r + j(), minY + j(), lineWidth, rng, 0.5)
  segmentsArc(minX + w - r, minY + r, r, -Math.PI / 2, 0)
  roughSegment(ctx, minX + w + j(), minY + r + j(), minX + w + j(), minY + h - r + j(), lineWidth, rng, 0.5)
  segmentsArc(minX + w - r, minY + h - r, r, 0, Math.PI / 2)
  roughSegment(ctx, minX + w - r + j(), minY + h + j(), minX + r + j(), minY + h + j(), lineWidth, rng, 0.5)
  segmentsArc(minX + r, minY + h - r, r, Math.PI / 2, Math.PI)
  roughSegment(ctx, minX + j(), minY + h - r + j(), minX + j(), minY + r + j(), lineWidth, rng, 0.5)
  segmentsArc(minX + r, minY + r, r, Math.PI, (Math.PI * 3) / 2)
  ctx.restore()
}

/** משושה — צומת */
export function drawRoughHexagon(ctx, cx, cy, rx, ry, color, lineWidth, seed) {
  if (rx < 2 && ry < 2) return
  const rng = mulberry32(seed >>> 0)
  const pts = []
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6
    pts.push({
      x: cx + rx * Math.cos(a) + (rng() - 0.5) * Math.max(1, lineWidth * 0.28),
      y: cy + ry * Math.sin(a) + (rng() - 0.5) * Math.max(1, lineWidth * 0.28),
    })
  }
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (let i = 0; i < 6; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % 6]
    roughSegment(ctx, a.x, a.y, b.x, b.y, lineWidth, rng, 0.5)
  }
  ctx.globalAlpha = 0.22
  ctx.lineWidth = lineWidth * 0.48
  ctx.beginPath()
  ctx.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < 6; i++) ctx.lineTo(pts[i].x, pts[i].y)
  ctx.closePath()
  ctx.stroke()
  ctx.restore()
}

/**
 * טקסט בסגנון שרטוט — כמה מילוי קל עם רעידה קלה
 */
export function drawRoughFlowText(ctx, text, cx, cy, { color, fontSize, seed }) {
  const lines = String(text || '').split(/\r?\n/).filter(Boolean)
  if (!lines.length) return
  const rng = mulberry32(seed >>> 0)
  const fs = Math.max(10, Math.min(96, fontSize))
  const lh = fs * 1.28
  const totalH = lines.length * lh
  let y = cy - totalH / 2 + lh / 2

  ctx.save()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `600 ${fs}px "Varela Round", "Heebo", system-ui, sans-serif`

  for (const line of lines) {
    for (let pass = 0; pass < 4; pass++) {
      const ox = (rng() - 0.5) * (pass === 3 ? 0.6 : 1.8)
      const oy = (rng() - 0.5) * (pass === 3 ? 0.6 : 1.8)
      ctx.globalAlpha = pass === 3 ? 1 : 0.16 + pass * 0.06
      ctx.fillStyle = color
      ctx.fillText(line, cx + ox, y + oy)
    }
    y += lh
  }
  ctx.globalAlpha = 1
  ctx.restore()
}

/**
 * @param {'arrow'|'line'|'rect'|'ellipse'|'diamond'|'roundRect'|'parallelogram'|'hexagon'|'text'|'erase'} kind
 */
export function drawRoughFlowShape(ctx, kind, x1, y1, x2, y2, { color, lineWidth, seed }) {
  const minX = Math.min(x1, x2)
  const minY = Math.min(y1, y2)
  const w = Math.abs(x2 - x1)
  const h = Math.abs(y2 - y1)
  if (w < 1 && h < 1) return

  switch (kind) {
    case 'line':
      drawRoughLine(ctx, x1, y1, x2, y2, color, lineWidth, seed)
      break
    case 'arrow':
      drawRoughArrow(ctx, x1, y1, x2, y2, color, lineWidth, seed)
      break
    case 'rect':
      drawRoughRect(ctx, minX, minY, w, h, color, lineWidth, seed)
      break
    case 'ellipse':
      drawRoughEllipse(ctx, minX + w / 2, minY + h / 2, w / 2, h / 2, color, lineWidth, seed)
      break
    case 'diamond':
      drawRoughDiamond(ctx, minX, minY, w, h, color, lineWidth, seed)
      break
    case 'roundRect':
      drawRoughRoundRect(ctx, minX, minY, w, h, color, lineWidth, seed)
      break
    case 'parallelogram':
      drawRoughParallelogram(ctx, minX, minY, w, h, color, lineWidth, seed)
      break
    case 'hexagon':
      drawRoughHexagon(ctx, minX + w / 2, minY + h / 2, w / 2, h / 2, color, lineWidth, seed)
      break
    default:
      drawRoughArrow(ctx, x1, y1, x2, y2, color, lineWidth, seed)
  }
}

// --------------------------------------------------------------------------------------
// Compatibility exports for FlowEditorModal (node-based pro flow editor).
// FlowEditorModal expects black rough strokes and a few geometry helpers.
// --------------------------------------------------------------------------------------

export function nodeCenter(n) {
  return { x: n.x + n.w / 2, y: n.y + n.h / 2 }
}

export function nodeHalfDiag(n) {
  return Math.hypot(n.w, n.h) / 2
}

export function shortenSegment(x1, y1, x2, y2, d1, d2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.hypot(dx, dy) || 1
  const r1 = Math.max(0, Math.min(1, d1 / len))
  const r2 = Math.max(0, Math.min(1, d2 / len))
  return {
    x1: x1 + dx * r1,
    y1: y1 + dy * r1,
    x2: x2 - dx * r2,
    y2: y2 - dy * r2,
  }
}

export function findNodeAtPoint(nodes, x, y) {
  if (!Array.isArray(nodes)) return null
  for (const n of nodes) {
    if (!n) continue

    if (n.type === 'rect') {
      if (x >= n.x && x <= n.x + n.w && y >= n.y && y <= n.y + n.h) return n
      continue
    }

    if (n.type === 'diamond') {
      const cx = n.x + n.w / 2
      const cy = n.y + n.h / 2
      const rx = n.w / 2
      const ry = n.h / 2
      const adx = Math.abs(x - cx) / (rx || 1)
      const ady = Math.abs(y - cy) / (ry || 1)
      if (adx + ady <= 1) return n
      continue
    }

    if (n.type === 'ellipse') {
      const cx = n.x + n.w / 2
      const cy = n.y + n.h / 2
      const rx = n.w / 2
      const ry = n.h / 2
      const dx = x - cx
      const dy = y - cy
      const v = (dx * dx) / ((rx || 1) * (rx || 1)) + (dy * dy) / ((ry || 1) * (ry || 1))
      if (v <= 1) return n
      continue
    }

    // Fallback: hit test by bounding box.
    if (x >= n.x && x <= n.x + n.w && y >= n.y && y <= n.y + n.h) return n
  }
  return null
}

const FLOW_EDITOR_BLACK = '#141414'

export function roughArrow(ctx, x1, y1, x2, y2, seed, lineWidth) {
  drawRoughArrow(ctx, x1, y1, x2, y2, FLOW_EDITOR_BLACK, lineWidth, seed)
}

export function roughRect(ctx, x, y, w, h, seed, lineWidth) {
  drawRoughRect(ctx, x, y, w, h, FLOW_EDITOR_BLACK, lineWidth, seed)
}

export function roughDiamondInRect(ctx, x, y, w, h, seed, lineWidth) {
  drawRoughDiamond(ctx, x, y, w, h, FLOW_EDITOR_BLACK, lineWidth, seed)
}

export function roughEllipseInRect(ctx, x, y, w, h, seed, lineWidth) {
  drawRoughEllipse(ctx, x + w / 2, y + h / 2, w / 2, h / 2, FLOW_EDITOR_BLACK, lineWidth, seed)
}
